/**
 * Performance Configuration for React Query and Image Optimization
 */
import React from 'react';
import { QueryClient } from '@tanstack/react-query';

/**
 * Optimized Query Client configuration for reduced memory usage
 */
export const createOptimizedQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Cache configuration for memory optimization
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes (updated from cacheTime to gcTime for React Query v5)
        
        // Network optimization
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: true,
        
        // Retry configuration
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 2; // Reduced retries for memory efficiency
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        // Mutation configuration
        retry: 1,
        retryDelay: 1000,
      }
    }
  });
};

/**
 * Image performance optimization settings
 */
export const imagePerformanceConfig = {
  // Intersection Observer settings
  lazyLoading: {
    threshold: 0.1,
    rootMargin: '50px',
  },
  
  // Image loading priorities
  criticalImageCount: 2,
  
  // Cache settings
  cache: {
    maxImages: 20,
    cleanupInterval: 5 * 60 * 1000, // 5 minutes
  },
  
  // Responsive breakpoints
  breakpoints: {
    mobile: 768,
    desktop: 1200,
  },
  
  // Animation settings
  transitions: {
    fadeIn: 'opacity 0.3s ease-in-out',
    slideIn: 'transform 0.3s ease-in-out',
  },
  
  // Hardware acceleration
  transform3d: 'translateZ(0)',
  willChange: 'opacity, transform',
};

/**
 * Memory monitoring utilities
 */
export const memoryMonitor = {
  // Get basic memory information (if available)
  getMemoryInfo: (): any => {
    // @ts-ignore - performance.memory is not in all browsers
    return (performance as any).memory || null;
  },
  
  // Log memory usage for debugging
  logMemoryUsage: (label: string = 'Memory Usage') => {
    const memInfo = memoryMonitor.getMemoryInfo();
    if (memInfo) {
      console.log(`[${label}]`, {
        used: `${(memInfo.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        total: `${(memInfo.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        limit: `${(memInfo.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`
      });
    }
  }
};

/**
 * Performance optimization hooks
 */
export const usePerformanceOptimization = () => {
  React.useEffect(() => {
    // Log initial memory usage
    memoryMonitor.logMemoryUsage('Component Mount');
    
    return () => {
      // Log memory usage on unmount
      memoryMonitor.logMemoryUsage('Component Unmount');
    };
  }, []);
  
  // Debounced window resize handler
  const [windowSize, setWindowSize] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 150); // Debounce resize events
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);
  
  return { windowSize };
};
